import { TextField } from '@ads/components-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toBrazilTime } from '@/features/notification/utils/toBrazilTime';
import dayjs from '@/shared/lib/dayjs';

import { DatePicker } from '@/shared/ui/atoms';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/**
 * Props do `ScheduleDateTimePicker`.
 */
export interface ScheduleDateTimePickerProps {
  /** ISO string atual do agendamento, ou `null` quando inválido/vazio */
  readonly value: string | null;
  /** Chamado com um ISO string futuro válido, ou `null` quando inválido/incompleto */
  readonly onChange: (scheduledAt: string | null) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildScheduledAt(dateIso: string, time: string): string | null {
  if (!dateIso || !time) return null;
  const parts = time.split(':');
  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  const dt = dayjs(
    `${dateIso}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`,
  );
  return dt.toISOString();
}

function isTimeFilled(time: string): boolean {
  return time.length === 5 && !time.includes('_');
}

function isTimeFormatValid(time: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(time);
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Molecule que encapsula os campos de data e hora para agendamento de notificação,
 * com validação de data passada, horário passado e formato inválido.
 *
 * @param value - ISO string do agendamento atual, ou `null` quando inválido/vazio
 * @param onChange - Chamado com um ISO string futuro válido, ou `null` quando inválido/incompleto
 *
 * @example
 * ```tsx
 * <ScheduleDateTimePicker
 *   value={schedule.scheduledAt}
 *   onChange={scheduledAt => updateSchedule({ scheduledAt })}
 * />
 * ```
 */
export function ScheduleDateTimePicker({ value, onChange }: ScheduleDateTimePickerProps) {
  const { t } = useTranslation('notification');
  const initialDate = toBrazilTime(value)?.format('YYYY-MM-DD') ?? '';
  const initialTime = toBrazilTime(value)?.format('HH:mm') ?? '';

  const [dateIso, setDateIso] = useState<string>(initialDate);
  const [dateInvalidError, setDateInvalidError] = useState(false);
  const [time, setTime] = useState<string>(initialTime);
  const [timeBlurred, setTimeBlurred] = useState(false);

  const pendingDateIsoRef = useRef(initialDate);

  // ─── Validação ────────────────────────────────────────────────────────────

  const today = dayjs().startOf('day');
  const parsedDate = dateIso ? dayjs(dateIso) : null;

  const isDateInvalid = dateInvalidError;
  const isDateInPast = !!parsedDate && parsedDate.isBefore(today);
  const isDateToday = !!parsedDate && parsedDate.isSame(today, 'day');

  const filled = isTimeFilled(time);
  const formatValid = !filled || isTimeFormatValid(time);

  const isTimeInPast =
    isDateToday && filled && formatValid && dayjs(`${dateIso}T${time}`).isBefore(dayjs());

  const dateError: string | undefined = isDateInvalid
    ? t('wizard.review.sendMode.errors.invalidDate')
    : isDateInPast
      ? t('wizard.review.sendMode.errors.dateInPast')
      : undefined;

  const timeError: string | undefined = !timeBlurred
    ? undefined
    : filled && !formatValid
      ? t('wizard.review.sendMode.errors.invalidTime')
      : isTimeInPast
        ? t('wizard.review.sendMode.errors.timeInPast')
        : undefined;

  // ─── Emissão ──────────────────────────────────────────────────────────────

  function emit(nextDate: string, nextTime: string) {
    const nextFilled = isTimeFilled(nextTime);
    const nextFormatValid = !nextFilled || isTimeFormatValid(nextTime);
    const nextParsed = nextDate ? dayjs(nextDate) : null;
    const nextDateInPast = !!nextParsed && nextParsed.isBefore(today);
    const nextDateToday = !!nextParsed && nextParsed.isSame(today, 'day');
    const nextTimeInPast =
      nextDateToday &&
      nextFilled &&
      nextFormatValid &&
      dayjs(`${nextDate}T${nextTime}`).isBefore(dayjs());

    const hasError = nextDateInPast || (nextFilled && !nextFormatValid) || nextTimeInPast;
    const scheduled = buildScheduledAt(nextDate, nextTime);
    onChange(!hasError && scheduled !== null ? scheduled : null);
  }

  const handleDateChange = (iso: string) => {
    pendingDateIsoRef.current = iso;
    setDateIso(iso);
    setDateInvalidError(false);
    emit(iso, time);
  };

  const handleInvalidDate = () => {
    setDateInvalidError(true);
  };

  const handleTimeChange = (newTime: string) => {
    setTime(newTime);
    emit(dateIso, newTime);
  };

  return (
    <div className="flex gap-xs mt-xs">
      <DatePicker
        label={t('wizard.review.sendMode.dateLabel')}
        size="sm"
        value={dateIso}
        onChange={handleDateChange}
        onInvalidInput={handleInvalidDate}
        hasError={!!dateError}
        {...(dateError !== undefined ? { helper: dateError } : {})}
        popoverSide="top"
        className="w-giant"
      />

      <div className="w-giant">
        <TextField
          label={t('wizard.review.sendMode.timeLabel')}
          size="sm"
          mask="99:99"
          value={time}
          onChange={e => handleTimeChange(e.target.value)}
          onBlur={() => setTimeBlurred(true)}
          placeholder={t('wizard.review.sendMode.timePlaceholder')}
          hasError={!!timeError}
          {...(timeError !== undefined ? { helper: timeError } : {})}
          fullWidth
        />
      </div>
    </div>
  );
}
